import { trackImpersonation } from "@/components/dashboard/ImpersonationBanner";
import { stopAndClearReplay } from "@/lib/replay";

export interface ImpersonateResult {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    image: string | null;
    onboardingCompletedAt?: string | null;
  };
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

/**
 * Start (or switch) an impersonation session. The backend swaps the
 * `rawiah.session` cookie to the target user's token and stashes the
 * admin's token in `rawiah.admin_session` (HttpOnly, restored by
 * `/api/admin/impersonate/stop`). We then hard-reload so the next server
 * render reads the new cookies.
 *
 * Auth comes from the admin's session cookie (auto-attached). Callers
 * don't need to pass tokens.
 */
export async function startImpersonation(
  targetUserId: string,
): Promise<ImpersonateResult> {
  const res = await fetch(`/api/admin/impersonate/${targetUserId}`, {
    method: "POST",
    credentials: "include",
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg =
      (err as { error?: { message?: string } }).error?.message ||
      (err as { error?: string }).error ||
      (res.status === 403
        ? "You don't have permission to impersonate."
        : "Impersonation failed");
    throw new Error(typeof msg === "string" ? msg : "Impersonation failed");
  }

  const { data } = (await res.json()) as { data: ImpersonateResult };

  trackImpersonation({
    id: data.user.id,
    name: data.user.name,
    role: data.user.role,
    image: data.user.image,
  });

  // Belt-and-suspenders: forget the admin's replay so the impersonated session
  // starts a clean, unchained replay (the identity guard would catch it too).
  stopAndClearReplay();

  // Land on the dashboard so the impersonated user sees their own
  // landing page rather than wherever the admin was.
  window.location.assign("/dashboard");
  return data;
}

/**
 * localStorage key consumed by `StopImpersonationToast` on the next
 * page render to display the "switched back to admin" notification.
 * Set immediately before the post-stop redirect so it survives the
 * hard reload.
 */
export const STOP_IMPERSONATION_TOAST_KEY = "rawiah.stoppedImpersonating";

/**
 * Reverse `startImpersonation` — POSTs `/api/admin/impersonate/stop`,
 * which reads the admin-stash cookie, restores it as the session, and
 * clears the stash. We then hard-reload to land on the admin dashboard.
 *
 * Stashes the impersonated user's display name in localStorage so the
 * destination page can show a toast confirming the switch back.
 */
export async function stopBackendImpersonation(
  redirectTo = "/dashboard",
  impersonatedName?: string | null,
): Promise<void> {
  await fetch("/api/admin/impersonate/stop", {
    method: "POST",
    credentials: "include",
  });
  try {
    localStorage.setItem(
      STOP_IMPERSONATION_TOAST_KEY,
      JSON.stringify({
        name: impersonatedName ?? null,
        at: Date.now(),
      }),
    );
  } catch {
    /* localStorage may be unavailable in private contexts — best effort */
  }
  // Returning to admin is another identity swap — start a fresh replay.
  stopAndClearReplay();
  window.location.assign(redirectTo);
}
